import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { environment } from 'environments/environment';
import { from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthResponse } from '../../auth-response.interface';
import { FirebaseService } from '../../firebase.service';
import { LocalStorage } from '../../local-storage.class';
import { Routenames } from '../../route-names.enum';
import { User } from '../../user.interface';
import { Utils } from '../../utils.class';
import { ApiService } from '../api/api.service';
import { getAuthDevice } from './auth-device';

@Injectable()
export class AuthService {
    private isRefreshingToken = false;

    private loggedIn = new EventEmitter<boolean>();
    onLogin = this.loggedIn.asObservable();

    constructor(
        private apiService: ApiService,
        private router: Router,
        private firebaseService: FirebaseService
    ) {}

    updateFirebaseToken(token) {
        return this.apiService.postCall$<boolean>('auth/firebase', { token });
    }

    login(email: string, password: string) {
        return this.apiService
            .postCall$<AuthResponse>('auth/login', { email, password, ...getAuthDevice() })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    loginEmail(email) {
        return this.apiService.postCall$<boolean>('auth/login-email', { email });
    }

    loginPin(email, pin) {
        return this.apiService
            .postCall$<AuthResponse>('auth/login-code', { pin, email, ...getAuthDevice() })
            .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
    }

    loginType(email: string) {
        return this.apiService.postCall$<{
            loginType: string;
        }>('auth/login-type', { email });
    }

    handleMicrosoftCallback(code: string, state: string) {
        return this.apiService
            .getCallData$<AuthResponse>('auth/microsoft/callback', {
                code,
                state,
                redirect_origin: this.microsoftRedirectOrigin(),
                ...getAuthDevice(),
            })
            .pipe(map(response => this.handleResponse(response)));
    }

    /**
     * Starts Microsoft login and passes the web origin that should receive the OAuth callback.
     *
     * In a normal browser this is the current page origin. In the native Capacitor app, window.location.origin
     * can be a non-web scheme such as capacitor://localhost, so we fall back to the configured API origin.
     * The backend validates the value before using it directly, or before routing a PR callback through Entra.
     */
    loginWithMicrosoft() {
        return this.apiService.getCallData$<{ auth_url: string }>('auth/microsoft/login', {
            redirect_origin: this.microsoftRedirectOrigin(),
        });
    }

    /**
     * Resolves the Firebase messaging token, giving up after `timeoutMs` so logout never hangs
     * waiting on a push-registration call that may never resolve.
     */
    private getFirebaseTokenWithTimeout(timeoutMs = 3000): Promise<string | null> {
        if (!Capacitor.isNativePlatform()) {
            return Promise.resolve(null);
        }

        const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), timeoutMs));
        const tokenRequest = FirebaseMessaging.getToken()
            .then(result => result.token)
            .catch(() => null);
        return Promise.race([tokenRequest, timeout]);
    }

    /**
     * Returns a Microsoft-compatible HTTP(S) origin for the callback route.
     */
    private microsoftRedirectOrigin(): string {
        const currentOrigin = window.location.origin;
        if (
            currentOrigin?.startsWith('http://') ||
            currentOrigin?.startsWith('https://') ||
            currentOrigin?.startsWith('capacitor://')
        ) {
            return currentOrigin;
        }

        // Native app shells expose a custom scheme; use the configured backend origin as the stable web callback.
        return new URL(environment.apiEndpoint).origin;
    }

    refresh(token?: string) {
        if (token) {
            // Explicit, user-initiated identity switch (take-over, back-to-parent): must never be
            // silently dropped by the background self-refresh mutex below.
            return this.apiService
                .postCall$<AuthResponse>('auth/refresh', { token })
                .pipe(map(response => this.handleResponse(response.data as AuthResponse)));
        }
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            return this.apiService.getCall$<AuthResponse>('auth/refresh').pipe(
                map(response => this.handleResponse(response.data)),
                catchError(error => {
                    this.isRefreshingToken = false;
                    return throwError(error);
                })
            );
        }
        return of();
    }

    switchToProfile(token: string, crossAppParent?: { token: string; user: User }) {
        const takeoverId = Utils.parseJwt(token)?.takeover;

        const parentToken = LocalStorage.getUserToken();
        const parentUser = LocalStorage.getUser();
        const existingParentToken = LocalStorage.getParentUserToken();
        const existingParentUser = LocalStorage.getParentUser();

        LocalStorage.logoutUser();
        if (parentUser && takeoverId === parentUser.id) {
            LocalStorage.setParentUser(parentToken, parentUser);
        } else if (existingParentToken && existingParentUser) {
            // Chained takeover: keep the original parent instead of the intermediate user we're switching away from.
            LocalStorage.setParentUser(existingParentToken, existingParentUser);
        } else if (crossAppParent) {
            // Take-over initiated from a different app/origin (e.g. the admin frontend): there is no
            // local session to read the parent from, so the initiating app hands us its identity directly.
            LocalStorage.setParentUser(crossAppParent.token, crossAppParent.user);
        }
        return this.refresh(token);
    }

    switchToParent() {
        const parentToken = LocalStorage.getParentUserToken();
        const switchBack = () => {
            LocalStorage.logoutUser();
            this.refresh(parentToken).subscribe({
                next: () => this.router.navigate(['/'], { replaceUrl: true }),
                error: () => {
                    LocalStorage.unsetParentUser();
                    this.router.navigate([Routenames.login], { replaceUrl: true });
                },
            });
        };
        // End the takeover session while its token is still the active one
        this.apiService.postCall$('auth/logout').subscribe({ next: switchBack, error: switchBack });
    }

    logout() {
        return from(this.getFirebaseTokenWithTimeout()).pipe(
            switchMap(token => {
                const data = {};
                if (token) {
                    data['firebaseToken'] = { firebaseToken: token };
                }

                this.firebaseService.unregisterFromAllTopics();
                this.firebaseService.deleteToken();
                return this.apiService.postCall$('auth/logout', data);
            }),
            map(response => {
                this.loggedIn.emit(false);
                LocalStorage.logoutUser();
                return response;
            }),
            catchError(error => {
                this.loggedIn.emit(false);
                LocalStorage.logoutUser();
                return throwError(error);
            })
        );
    }

    public getUserDetails() {
        return this.apiService.getCall$<User>('auth/me').pipe(
            tap(user => {
                const oldUser = LocalStorage.getUser();
                this.setUser(user.data);
                this.loggedIn.emit(true);
            })
        );
    }

    public setLocalstorage(user: User, accessTokenData: object) {
        LocalStorage.setUserToken(accessTokenData['access_token']);
        LocalStorage.setExpireTimeToken(accessTokenData['expires_in']);
        this.setUser(user);
    }

    private setUser(user: User) {
        LocalStorage.setUser(user);

        this.firebaseService.subscribeToTopic('my-workareas-' + user.id);
    }

    private handleResponse(authResponse: AuthResponse): AuthResponse {
        LocalStorage.setUserToken(authResponse.access_token);
        LocalStorage.setUser(authResponse.user);
        LocalStorage.setExpireTimeToken(authResponse.expires_in);
        this.setUser(authResponse.user);
        if (Capacitor.isNativePlatform()) {
            FirebaseMessaging.getToken()
                .then(result => {
                    FirebaseService.token = result.token;
                    this.updateFirebaseToken(FirebaseService.token).subscribe();
                })
                .catch(err => console.error(err));

            FirebaseMessaging.addListener('tokenReceived', event => {
                FirebaseService.token = event.token;
                this.updateFirebaseToken(FirebaseService.token).subscribe();
            });
            this.firebaseService.subscribeToTopic('my-workareas-' + authResponse.user.id);
        }
        this.loggedIn.emit(true);
        this.isRefreshingToken = false;
        return authResponse;
    }
}
